import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import type { ApolloCache } from "@apollo/client";
import {
  ExternalVisibility,
  GetModernizedCoursePublishHistoryDocument,
  ModernizedActivityEntityType,
  ModernizedActivityStatus,
  type GetModernizedCoursePublishHistoryQuery,
  type UpdateModernizedActivityFieldsMutation,
  type UpdateModernizedActivityFieldsMutationVariables,
  useClearModernizedCoursePublishHistoryMutation,
  useGetModernizedCoursePublishHistoryQuery,
  usePublishModernizedActivitiesMutation,
  useUpdateModernizedActivityFieldsMutation,
} from "@axio/graphql/generated/graphql";

import CustomAdminModal from "../../../../components/common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../../../components/common/general/CustomText/CustomText";

import ReviewPanel from "./ReviewPanel";
import type {
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewItemType,
  CourseEvaluatorReviewSidebarGroup,
  CourseEvaluatorReviewSidebarItem,
} from "./types/reviewTypes";
import type {
  ActivityDetail,
  PublishedPublishItem,
  PublishManualStep,
  PublishStatus,
  QueuedPublishItem,
  StructuredFields,
} from "./types/reviewTypes";

interface DecisionEntry {
  decision: CourseEvaluatorReviewDecision;
}

function getSidebarItemType(item: CourseEvaluatorReviewSidebarItem): CourseEvaluatorReviewItemType | undefined {
  const itemTypeMeta = item.meta?.find(
    (meta): meta is { kind: "itemType"; itemType: CourseEvaluatorReviewItemType } => meta.kind === "itemType"
  );
  return itemTypeMeta?.itemType;
}

function getSidebarItemIsNew(item: CourseEvaluatorReviewSidebarItem): boolean {
  return Boolean(item.meta?.some((meta) => meta.kind === "tag" && meta.tag === "new"));
}

function getItemTypeLabel(itemType: CourseEvaluatorReviewItemType): string {
  switch (itemType) {
    case "externalLink":
      return "External Link";
    case "moduleDescription":
      return "Module Description";
    case "discussion":
      return "Discussion";
    case "checklist":
      return "Checklist";
    case "quiz":
      return "Quiz";
    case "file":
      return "File";
    case "htmlContent":
      return "HTML Content";
    case "link":
      return "External URL";
    case "ile":
      return "Axio ILE";
  }
}

/** Map sidebar item kind to the category label shown in the publish sheet. */
function getCategoryLabel(item: CourseEvaluatorReviewSidebarItem): string {
  const itemType = getSidebarItemType(item);
  const isNew = getSidebarItemIsNew(item);

  if (item.entityType === ModernizedActivityEntityType.Assessment || itemType === "quiz") {
    return "Quiz";
  }

  switch (item.entityType) {
    case ModernizedActivityEntityType.Assignment:
      if (itemType === "checklist" || itemType === "file" || itemType === "link") {
        return getItemTypeLabel(itemType);
      }
      return "Assignment";
    case ModernizedActivityEntityType.Module:
      return "Module Description";
    case ModernizedActivityEntityType.ContentSection:
      return "Course Content";
    case ModernizedActivityEntityType.Project:
      return isNew ? "Page" : "Assignment";
    case ModernizedActivityEntityType.OtherActivity:
      if (itemType === "file") {
        return "File Topic";
      }
      if (itemType) {
        return getItemTypeLabel(itemType);
      }
      return "Activity";
    case ModernizedActivityEntityType.Assessment:
      return "Quiz";
  }

  switch (item.kind) {
    case "moduleDescription":
      return "Module Description";
    case "recommendation":
      return "Recommendation";
    case "assignment":
      return "Assignment";
    case "suggestedContent":
      return "Suggested Content";
    default:
      return "Change";
  }
}

interface UndoEntry {
  itemId: string;
  previous: DecisionEntry;
}

interface CourseEvaluatorReviewContainerProps {
  review: CourseEvaluatorReviewData;
  activityDetails: Record<string, ActivityDetail>;
  onExitReview: () => void;
}

type ModernizedActivityCacheTypename =
  | "ModernizedAssignment"
  | "ModernizedModule"
  | "ModernizedOtherActivity"
  | "ModernizedCourseContentSection"
  | "ModernizedAssessment"
  | "ModernizedProject";

type PublishHistoryRecord =
  GetModernizedCoursePublishHistoryQuery["getModernizedCoursePublishHistory"]["records"][number];
type PublishHistoryItem = PublishHistoryRecord["autoPublishedItems"][number];
type PublishHistoryManualStep = PublishHistoryRecord["manualSteps"][number];

function mapPublishHistoryItem(item: PublishHistoryItem): PublishedPublishItem {
  return {
    id: item.id,
    title: item.title,
    category: item.category,
    moduleName: item.moduleName ?? "Course-Level Content",
  };
}

function mapPublishHistoryManualStep(step: PublishHistoryManualStep): PublishManualStep {
  return {
    id: step.id,
    title: step.title,
    action: step.action,
    detail: step.detail,
    externalUrl: step.externalUrl,
    downloadUrl: step.downloadUrl,
  };
}

function getActivityCacheTypename(entityType: ModernizedActivityEntityType): ModernizedActivityCacheTypename {
  switch (entityType) {
    case ModernizedActivityEntityType.Assignment:
      return "ModernizedAssignment";
    case ModernizedActivityEntityType.Module:
      return "ModernizedModule";
    case ModernizedActivityEntityType.OtherActivity:
      return "ModernizedOtherActivity";
    case ModernizedActivityEntityType.ContentSection:
      return "ModernizedCourseContentSection";
    case ModernizedActivityEntityType.Assessment:
      return "ModernizedAssessment";
    case ModernizedActivityEntityType.Project:
      return "ModernizedProject";
  }
}

function updateActivityStatusInCache(
  cache: ApolloCache<UpdateModernizedActivityFieldsMutation>,
  item: CourseEvaluatorReviewSidebarItem,
  status: ModernizedActivityStatus
): void {
  const cacheId = cache.identify({
    __typename: getActivityCacheTypename(item.entityType),
    id: item.id,
  });

  if (!cacheId) {
    return;
  }

  cache.modify({
    id: cacheId,
    fields: {
      status: () => status,
    },
  });
}

function getDecisionFromStatus(status: ModernizedActivityStatus | null | undefined): CourseEvaluatorReviewDecision {
  switch (status) {
    case ModernizedActivityStatus.Queued:
      return "accepted";
    case ModernizedActivityStatus.Skipped:
      return "rejected";
    case ModernizedActivityStatus.Published:
      return "synced";
    default:
      return null;
  }
}

function getStatusFromDecision(decision: CourseEvaluatorReviewDecision): ModernizedActivityStatus {
  switch (decision) {
    case "accepted":
    case "edited":
      return ModernizedActivityStatus.Queued;
    case "rejected":
      return ModernizedActivityStatus.Skipped;
    case "synced":
      return ModernizedActivityStatus.Published;
    default:
      return ModernizedActivityStatus.Undecided;
  }
}

function buildInitialDecisions(sidebarGroups: CourseEvaluatorReviewSidebarGroup[]): Map<string, DecisionEntry> {
  const initialDecisions = new Map<string, DecisionEntry>();

  sidebarGroups.forEach((group) => {
    group.items.forEach((item) => {
      const decision = getDecisionFromStatus(item.status);
      if (decision) {
        initialDecisions.set(item.id, { decision });
      }
    });
  });

  return initialDecisions;
}

function getDecisionSourceSignature(sidebarGroups: CourseEvaluatorReviewSidebarGroup[]): string {
  return sidebarGroups
    .map((group) => `${group.id}:${group.items.map((item) => `${item.id}:${item.status ?? ""}`).join(",")}`)
    .join("|");
}

function getEditMutationVariables(
  item: CourseEvaluatorReviewSidebarItem,
  editedFields: StructuredFields
): UpdateModernizedActivityFieldsMutationVariables | null {
  switch (item.entityType) {
    case ModernizedActivityEntityType.Assignment:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedTitle: editedFields.title,
        recommendedDescription: editedFields.description,
        recommendedPointsPossible: editedFields.points,
      };
    case ModernizedActivityEntityType.Module:
    case ModernizedActivityEntityType.OtherActivity:
    case ModernizedActivityEntityType.Project:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedTitle: editedFields.title,
        recommendedDescription: editedFields.description,
      };
    case ModernizedActivityEntityType.ContentSection:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedValue: editedFields.description,
      };
    case ModernizedActivityEntityType.Assessment:
      return null;
  }

  return null;
}

function CourseEvaluatorReviewContainer({
  review,
  activityDetails,
  onExitReview,
}: CourseEvaluatorReviewContainerProps) {
  const [publishModernizedActivitiesMutation] = usePublishModernizedActivitiesMutation();
  const [updateModernizedActivityFieldsMutation] = useUpdateModernizedActivityFieldsMutation();
  const [clearModernizedCoursePublishHistoryMutation, { loading: isClearingPublishHistory }] =
    useClearModernizedCoursePublishHistoryMutation();
  const { data: publishHistoryData, refetch: refetchPublishHistory } = useGetModernizedCoursePublishHistoryQuery({
    variables: { courseId: review.courseId },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<"all" | "undecided">("all");
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<Set<string>>(
    () => new Set(review.sidebarGroups.filter((group) => !group.defaultExpanded).map((group) => group.id))
  );
  const [decisions, setDecisions] = useState<Map<string, DecisionEntry>>(() =>
    buildInitialDecisions(review.sidebarGroups)
  );
  const [undoEntry, setUndoEntry] = useState<UndoEntry | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishSheetOpen, setIsPublishSheetOpen] = useState(false);
  const [isPublishHistoryOpen, setIsPublishHistoryOpen] = useState(false);
  const [isLivePublishWarningVisible, setIsLivePublishWarningVisible] = useState(false);
  const [publishStatus, setPublishStatus] = useState<PublishStatus>("idle");
  const [liveActivityDetails, setLiveActivityDetails] = useState<Record<string, ActivityDetail>>(activityDetails);
  /** Publish history record shown in the success state, captured after publish refetch. */
  const [lastPublishRecord, setLastPublishRecord] = useState<PublishHistoryRecord | null>(null);
  const decisionSourceSignature = useMemo(
    () => getDecisionSourceSignature(review.sidebarGroups),
    [review.sidebarGroups]
  );

  // Ref to track latest decisions for non-blocking auto-advance
  const decisionsRef = useRef(decisions);
  decisionsRef.current = decisions;

  const isMountedRef = useRef(true);
  const decisionSourceSignatureRef = useRef(decisionSourceSignature);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (decisionSourceSignatureRef.current === decisionSourceSignature) {
      return;
    }

    decisionSourceSignatureRef.current = decisionSourceSignature;
    setDecisions(buildInitialDecisions(review.sidebarGroups));
  }, [decisionSourceSignature, review.sidebarGroups]);

  useEffect(() => {
    setLiveActivityDetails(activityDetails);
  }, [activityDetails]);

  const allItems = useMemo(() => review.sidebarGroups.flatMap((group) => group.items), [review.sidebarGroups]);

  const orderedIds = useMemo(() => allItems.map((item) => item.id), [allItems]);

  /** Lookup table mapping each item id to its enclosing module info. */
  const itemModuleIndex = useMemo(() => {
    const map = new Map<string, { moduleId: string | null; moduleName: string; moduleExternalUrl?: string }>();
    review.sidebarGroups.forEach((group) => {
      const moduleId = group.id === "course-level-content" ? null : group.id;
      group.items.forEach((item) => {
        map.set(item.id, { moduleId, moduleName: group.title, moduleExternalUrl: group.externalUrl });
      });
    });
    return map;
  }, [review.sidebarGroups]);

  const liveSidebarGroups = useMemo<CourseEvaluatorReviewSidebarGroup[]>(() => {
    return review.sidebarGroups
      .map((group) => {
        const decided = group.items.filter((item) => decisions.get(item.id)?.decision !== undefined).length;
        const items =
          filterMode === "undecided"
            ? group.items.filter((item) => decisions.get(item.id)?.decision === undefined)
            : group.items;

        return { ...group, decided, items };
      })
      .filter((group) => filterMode === "all" || group.items.length > 0);
  }, [review.sidebarGroups, filterMode, decisions]);

  const selectedSidebarItem = useMemo<CourseEvaluatorReviewSidebarItem | null>(() => {
    if (!selectedItemId) return null;
    return allItems.find((item) => item.id === selectedItemId) || null;
  }, [allItems, selectedItemId]);

  const getDecision = useCallback(
    (id: string): CourseEvaluatorReviewDecision => decisions.get(id)?.decision ?? null,
    [decisions]
  );

  const selectedDecision = selectedItemId ? getDecision(selectedItemId) : null;

  // --- Stats ---
  const stats = useMemo(() => {
    let accepted = 0;
    let rejected = 0;
    let published = 0;
    allItems.forEach((item) => {
      const d = decisions.get(item.id)?.decision;
      if (d === "accepted" || d === "edited") accepted++;
      if (d === "rejected") rejected++;
      if (d === "synced") published++;
    });
    const decided = accepted + rejected + published;
    return { decided, accepted, rejected, published, total: allItems.length };
  }, [allItems, decisions]);

  const reviewedProgress = stats.total > 0 ? (stats.decided / stats.total) * 100 : 0;

  const publishHistoryRecords = useMemo<PublishHistoryRecord[]>(() => {
    const result = publishHistoryData?.getModernizedCoursePublishHistory;
    if (!result?.success) {
      return [];
    }

    return result.records;
  }, [publishHistoryData]);

  const pendingManualStepsCount = useMemo(
    () =>
      publishHistoryRecords.reduce(
        (sum, record) => sum + Math.max(record.manualStepCount - record.completedManualStepCount, 0),
        0
      ),
    [publishHistoryRecords]
  );

  // --- Queued items for publish sheet ---
  const queuedPublishItems = useMemo<QueuedPublishItem[]>(() => {
    return allItems
      .filter((item) => {
        const d = decisions.get(item.id)?.decision;
        return d === "accepted" || d === "edited";
      })
      .map((item) => {
        const mod = itemModuleIndex.get(item.id);
        const detail = liveActivityDetails[item.id];
        const moduleName =
          mod?.moduleId != null
            ? (liveActivityDetails[mod.moduleId]?.recommendedTitle ?? mod.moduleName)
            : (mod?.moduleName ?? "Course-Level Content");
        return {
          id: item.id,
          title: detail?.recommendedTitle ?? item.title,
          kind: item.kind,
          entityType: item.entityType,
          itemType: getSidebarItemType(item),
          isNew: getSidebarItemIsNew(item),
          category: getCategoryLabel(item),
          moduleId: mod?.moduleId ?? null,
          moduleName,
          moduleExternalUrl: mod?.moduleExternalUrl,
          externalUrl: detail?.url,
          attachments: detail?.attachments,
          wasEdited: decisions.get(item.id)?.decision === "edited",
          externalVisibility: item.externalVisibility,
        };
      });
  }, [allItems, decisions, itemModuleIndex, liveActivityDetails]);

  // Override review data with live stats + filtered sidebar
  const liveReview = useMemo<CourseEvaluatorReviewData>(
    () => ({
      ...review,
      reviewedCount: stats.decided,
      queuedCount: stats.accepted,
      skippedCount: stats.rejected,
      publishedCount: stats.published,
      sidebarGroups: liveSidebarGroups,
    }),
    [review, stats, liveSidebarGroups]
  );

  // --- Non-blocking auto-advance ---
  const advanceToNextUndecided = useCallback(
    (fromId: string) => {
      // Use requestAnimationFrame to keep the UI responsive
      requestAnimationFrame(() => {
        if (!isMountedRef.current) return;

        const currentDecisions = decisionsRef.current;
        const currentIdx = orderedIds.indexOf(fromId);

        // Search forward first
        for (let i = currentIdx + 1; i < orderedIds.length; i++) {
          const id = orderedIds[i];
          if (!currentDecisions.get(id)?.decision) {
            setSelectedItemId(id);
            return;
          }
        }
        // Wrap around
        for (let i = 0; i < currentIdx; i++) {
          const id = orderedIds[i];
          if (!currentDecisions.get(id)?.decision) {
            setSelectedItemId(id);
            return;
          }
        }
        // All decided — stay on current or go to overview
      });
    },
    [orderedIds]
  );

  const persistDecision = useCallback(
    async (item: CourseEvaluatorReviewSidebarItem, decision: CourseEvaluatorReviewDecision) => {
      const status = getStatusFromDecision(decision);
      const result = await updateModernizedActivityFieldsMutation({
        variables: {
          entityId: item.id,
          entityType: item.entityType,
          status,
        },
        optimisticResponse: {
          updateModernizedActivityFields: {
            __typename: "ModernizedEditLogResult",
            success: true,
          },
        },
        update: (cache) => updateActivityStatusInCache(cache, item, status),
      });

      if (!result.data?.updateModernizedActivityFields.success) {
        throw new Error("updateModernizedActivityFields mutation failed");
      }
    },
    [updateModernizedActivityFieldsMutation]
  );

  const markSaved = useCallback(() => {
    const now = new Date();
    setLastSavedAt(now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
  }, []);

  const handleSaveEdit = useCallback(
    async (item: CourseEvaluatorReviewSidebarItem, editedFields: StructuredFields) => {
      if (decisionsRef.current.get(item.id)?.decision === "synced") return;

      const variables = getEditMutationVariables(item, editedFields);
      if (!variables) return;

      setIsSaving(true);
      try {
        const result = await updateModernizedActivityFieldsMutation({ variables });
        if (!result.data?.updateModernizedActivityFields.success) {
          throw new Error("updateModernizedActivityFields mutation failed");
        }
        if (!isMountedRef.current) return;

        setLiveActivityDetails((prev) => {
          const current = prev[item.id];
          if (!current) return prev;

          return {
            ...prev,
            [item.id]: {
              ...current,
              recommendedTitle: editedFields.title,
              recommendedDescription: editedFields.description,
              recommendedPointsPossible: editedFields.points,
            },
          };
        });
        markSaved();
      } catch {
        if (!isMountedRef.current) return;
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [markSaved, updateModernizedActivityFieldsMutation]
  );

  // --- Actions ---
  const makeDecision = useCallback(
    async (item: CourseEvaluatorReviewSidebarItem, decision: CourseEvaluatorReviewDecision) => {
      const itemId = item.id;
      const previous = decisionsRef.current.get(itemId) ?? { decision: null };

      setUndoEntry({ itemId, previous });
      setDecisions((prev) => {
        const next = new Map(prev);
        next.set(itemId, { decision });
        return next;
      });

      advanceToNextUndecided(itemId);

      setIsSaving(true);
      try {
        await persistDecision(item, decision);
        if (!isMountedRef.current) return;
        markSaved();
      } catch {
        if (!isMountedRef.current) return;
        setDecisions((prev) => {
          const next = new Map(prev);
          if (previous.decision === null) {
            next.delete(itemId);
          } else {
            next.set(itemId, previous);
          }
          return next;
        });
        setUndoEntry(null);
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [advanceToNextUndecided, persistDecision, markSaved]
  );

  const handleAccept = useCallback(
    (item: CourseEvaluatorReviewSidebarItem) => {
      void makeDecision(item, "accepted");
    },
    [makeDecision]
  );
  const handleReject = useCallback(
    (item: CourseEvaluatorReviewSidebarItem) => {
      void makeDecision(item, "rejected");
    },
    [makeDecision]
  );

  const handleUndo = useCallback(async () => {
    if (!undoEntry) return;
    const item = allItems.find((candidate) => candidate.id === undoEntry.itemId);
    if (!item) return;
    const current = decisions.get(undoEntry.itemId) ?? { decision: null };

    setDecisions((prev) => {
      const next = new Map(prev);
      if (undoEntry.previous.decision === null) {
        next.delete(undoEntry.itemId);
      } else {
        next.set(undoEntry.itemId, undoEntry.previous);
      }
      return next;
    });
    setSelectedItemId(undoEntry.itemId);
    setUndoEntry(null);

    setIsSaving(true);
    try {
      await persistDecision(item, undoEntry.previous.decision);
      if (!isMountedRef.current) return;
      markSaved();
    } catch {
      if (!isMountedRef.current) return;
      setDecisions((prev) => {
        const next = new Map(prev);
        if (current.decision === null) {
          next.delete(undoEntry.itemId);
        } else {
          next.set(undoEntry.itemId, current);
        }
        return next;
      });
      setUndoEntry(undoEntry);
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [undoEntry, allItems, decisions, persistDecision, markSaved]);

  const handleNavigateNext = useCallback(() => {
    if (!selectedItemId) {
      if (orderedIds.length > 0) setSelectedItemId(orderedIds[0]);
      return;
    }
    const idx = orderedIds.indexOf(selectedItemId);
    if (idx < orderedIds.length - 1) setSelectedItemId(orderedIds[idx + 1]);
  }, [selectedItemId, orderedIds]);

  const handleNavigatePrev = useCallback(() => {
    if (!selectedItemId) return;
    const idx = orderedIds.indexOf(selectedItemId);
    if (idx > 0) setSelectedItemId(orderedIds[idx - 1]);
    else setSelectedItemId(null);
  }, [selectedItemId, orderedIds]);

  // --- Publish flow ---
  const handleOpenPublishSheet = useCallback(() => {
    if (queuedPublishItems.length === 0) return;
    setPublishStatus("idle");
    setLastPublishRecord(null);
    setIsPublishHistoryOpen(false);
    setIsPublishSheetOpen(true);
  }, [queuedPublishItems.length]);

  const handleClosePublishSheet = useCallback(() => {
    if (publishStatus === "publishing") return;
    setIsPublishSheetOpen(false);
  }, [publishStatus]);

  /** Closes the success state and returns the review page to its normal view. */
  const handleDonePublish = useCallback(() => {
    setIsPublishSheetOpen(false);
    setPublishStatus("idle");
    setLastPublishRecord(null);
  }, []);

  const handleOpenPublishHistory = useCallback(() => {
    setIsPublishSheetOpen(false);
    setIsPublishHistoryOpen(true);
  }, []);

  const handleClosePublishHistory = useCallback(() => {
    setIsPublishHistoryOpen(false);
  }, []);

  const handleClearPublishHistory = useCallback(async () => {
    try {
      const result = await clearModernizedCoursePublishHistoryMutation({
        variables: { courseId: review.courseId },
        refetchQueries: [
          {
            query: GetModernizedCoursePublishHistoryDocument,
            variables: { courseId: review.courseId },
          },
        ],
        awaitRefetchQueries: true,
      });

      if (!result.data?.clearModernizedCoursePublishHistory.success) {
        throw new Error("clearModernizedCoursePublishHistory mutation failed");
      }
    } catch {
      if (!isMountedRef.current) return;
    }
  }, [clearModernizedCoursePublishHistoryMutation, review.courseId]);

  const markItemsAsPublished = useCallback((itemsToMark: QueuedPublishItem[], publishRecord: PublishHistoryRecord) => {
    const publishedIds = new Set(itemsToMark.map((item) => item.id));
    setDecisions((prev) => {
      const next = new Map(prev);
      itemsToMark.forEach((item) => {
        if (publishedIds.has(item.id)) {
          next.set(item.id, { decision: "synced" });
        }
      });
      return next;
    });
    setLastPublishRecord(publishRecord);
    setPublishStatus("done");
  }, []);

  const lastPublishedItems = useMemo<PublishedPublishItem[]>(
    () => lastPublishRecord?.autoPublishedItems.map(mapPublishHistoryItem) ?? [],
    [lastPublishRecord]
  );

  const lastPublishManualSteps = useMemo<PublishManualStep[]>(
    () => lastPublishRecord?.manualSteps.map(mapPublishHistoryManualStep) ?? [],
    [lastPublishRecord]
  );

  const hasLiveItems = useMemo(
    () => queuedPublishItems.some((item) => item.externalVisibility === ExternalVisibility.Published),
    [queuedPublishItems]
  );

  const executePublish = useCallback(async () => {
    if (queuedPublishItems.length === 0) return;
    const snapshot = [...queuedPublishItems];
    setPublishStatus("publishing");
    try {
      const mutationResult = await publishModernizedActivitiesMutation({
        variables: {
          courseId: review.courseId,
          itemIds: snapshot.map((item) => item.id),
        },
      });
      if (!isMountedRef.current) return;
      const publishResult = mutationResult.data?.publishModernizedActivities;
      if (!publishResult?.success || !publishResult.publishEventId) {
        throw new Error("publishModernizedActivities mutation failed");
      }

      const publishedIds = new Set(publishResult.items.map((item) => item.id));
      if (publishedIds.size !== snapshot.length) {
        throw new Error("publishModernizedActivities returned partial item results");
      }

      const historyResult = await refetchPublishHistory({ courseId: review.courseId });
      if (!isMountedRef.current) return;
      const publishHistoryResult = historyResult.data?.getModernizedCoursePublishHistory;
      if (!publishHistoryResult?.success) {
        throw new Error("getModernizedCoursePublishHistory refetch failed");
      }

      const publishRecord = publishHistoryResult.records.find((record) => record.id === publishResult.publishEventId);
      if (!publishRecord) {
        throw new Error("publish history record was not returned for the latest publish event");
      }

      markItemsAsPublished(
        snapshot.filter((item) => publishedIds.has(item.id)),
        publishRecord
      );
    } catch {
      if (!isMountedRef.current) return;
      setPublishStatus("error");
    }
  }, [
    markItemsAsPublished,
    publishModernizedActivitiesMutation,
    queuedPublishItems,
    refetchPublishHistory,
    review.courseId,
  ]);

  const handlePublish = useCallback(() => {
    if (hasLiveItems) {
      setIsLivePublishWarningVisible(true);
      return;
    }
    void executePublish();
  }, [hasLiveItems, executePublish]);

  const handleConfirmLivePublish = useCallback(() => {
    setIsLivePublishWarningVisible(false);
    void executePublish();
  }, [executePublish]);

  const handleCancelLivePublish = useCallback(() => {
    setIsLivePublishWarningVisible(false);
  }, []);

  const toggleSection = (sectionId: string) => {
    setCollapsedSectionIds((previous) => {
      const next = new Set(previous);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  return (
    <>
      <ReviewPanel
        review={liveReview}
        activityDetails={liveActivityDetails}
        selectedItemId={selectedItemId}
        selectedReviewItem={selectedSidebarItem}
        selectedDecision={selectedDecision}
        filterMode={filterMode}
        collapsedSectionIds={collapsedSectionIds}
        reviewedProgress={reviewedProgress}
        getDecision={getDecision}
        lastSavedAt={lastSavedAt}
        isSaving={isSaving}
        onExitReview={onExitReview}
        onSelectItem={setSelectedItemId}
        onToggleSection={toggleSection}
        onFilterModeChange={setFilterMode}
        onStartReviewing={() => {
          // Select first undecided item
          const firstUndecided = orderedIds.find((id) => !decisions.get(id)?.decision);
          if (firstUndecided) setSelectedItemId(firstUndecided);
        }}
        onAccept={handleAccept}
        onReject={handleReject}
        onUndo={undoEntry ? handleUndo : undefined}
        onSaveEdit={handleSaveEdit}
        onNavigateNext={handleNavigateNext}
        onNavigatePrev={handleNavigatePrev}
        queuedPublishItems={queuedPublishItems}
        lastPublishedItems={lastPublishedItems}
        publishManualSteps={lastPublishManualSteps}
        undecidedCount={stats.total - stats.decided}
        isPublishSheetOpen={isPublishSheetOpen}
        publishStatus={publishStatus}
        onOpenPublishSheet={handleOpenPublishSheet}
        onClosePublishSheet={handleClosePublishSheet}
        onDonePublish={handleDonePublish}
        onPublish={handlePublish}
        publishHistoryRecords={publishHistoryRecords}
        pendingManualStepsCount={pendingManualStepsCount}
        isPublishHistoryOpen={isPublishHistoryOpen}
        isClearingPublishHistory={isClearingPublishHistory}
        onOpenPublishHistory={handleOpenPublishHistory}
        onClosePublishHistory={handleClosePublishHistory}
        onClearPublishHistory={handleClearPublishHistory}
      />
      <CustomAdminModal
        visible={isLivePublishWarningVisible}
        onRequestClose={handleCancelLivePublish}
        title="Publishing to a Live Course"
        ariaLabel="Live Course Publish Warning">
        <View style={styles.warningModalContent}>
          <CustomText
            text={`This course is currently published and visible to enrolled students in ${review.sourceLabel}. Any changes you publish will go live immediately and students will see them right away.`}
            useTranslationText={false}
            size="s"
            style={styles.warningModalText}
          />
          <View style={styles.warningModalButtons}>
            <Pressable
              onPress={handleCancelLivePublish}
              style={styles.warningModalCancelButton}
              accessibilityRole="button">
              <CustomText
                text="Back"
                useTranslationText={false}
                size="s"
                weight="medium"
                style={styles.warningModalCancelText}
              />
            </Pressable>
            <Pressable
              onPress={handleConfirmLivePublish}
              style={styles.warningModalConfirmButton}
              accessibilityRole="button">
              <CustomText
                text="Publish Anyway"
                useTranslationText={false}
                size="s"
                weight="medium"
                style={styles.warningModalConfirmText}
              />
            </Pressable>
          </View>
        </View>
      </CustomAdminModal>
    </>
  );
}

const styles = StyleSheet.create({
  warningModalContent: {
    padding: 24,
    gap: 24,
  },
  warningModalText: {
    color: "#344054",
    lineHeight: 22,
  },
  warningModalButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
  },
  warningModalCancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    backgroundColor: "#FFFFFF",
  },
  warningModalCancelText: {
    color: "#344054",
  },
  warningModalConfirmButton: {
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 8,
    backgroundColor: "#B42318",
  },
  warningModalConfirmText: {
    color: "#FFFFFF",
  },
});

export default CourseEvaluatorReviewContainer;
